import React, { useEffect, useRef, useState } from "react";
import {
  CheckCircleIcon,
  ExclamationCircleIcon,
} from "@heroicons/react/20/solid";

type SaveStatus = "idle" | "saving" | "saved" | "error";

interface ReceivingGroupInlineInputProps {
  ariaLabel: string;
  value: string | number | null | undefined;
  onCommit: (value: number) => Promise<boolean>;
  currency?: boolean;
  suffix?: string;
}

export default function ReceivingGroupInlineInput({
  ariaLabel,
  value,
  onCommit,
  currency = false,
  suffix,
}: ReceivingGroupInlineInputProps) {
  const normalizedValue = value == null || value === "" ? "" : String(value);
  const [draft, setDraft] = useState(normalizedValue);
  const [status, setStatus] = useState<SaveStatus>("idle");
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const savedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const draftRef = useRef(draft);

  useEffect(() => {
    setDraft(normalizedValue);
    draftRef.current = normalizedValue;
  }, [normalizedValue]);

  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
    },
    [],
  );

  const commit = async () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    const nextValue = Number(draftRef.current);
    if (
      !Number.isFinite(nextValue) ||
      nextValue < 0 ||
      draftRef.current === ""
    ) {
      setStatus("error");
      return;
    }
    if (nextValue === Number(value)) return;

    setStatus("saving");
    const success = await onCommit(nextValue);
    setStatus(success ? "saved" : "error");
    if (success) {
      if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
      savedTimerRef.current = setTimeout(() => setStatus("idle"), 1400);
    }
  };

  const scheduleCommit = (nextDraft: string) => {
    draftRef.current = nextDraft;
    setDraft(nextDraft);
    setStatus("idle");
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => void commit(), 450);
  };

  return (
    <div
      className="flex min-w-[112px] items-center justify-end gap-1"
      onClick={(event) => event.stopPropagation()}
    >
      {currency ? <span className="text-[#64748B]">$</span> : null}
      <input
        type="number"
        min="0"
        step="any"
        aria-label={ariaLabel}
        value={draft}
        onChange={(event) => scheduleCommit(event.target.value)}
        onBlur={() => void commit()}
        onKeyDown={(event) => {
          event.stopPropagation();
          if (event.key === "Enter") event.currentTarget.blur();
          if (event.key === "Escape") {
            if (timerRef.current) clearTimeout(timerRef.current);
            draftRef.current = normalizedValue;
            setDraft(normalizedValue);
            setStatus("idle");
            event.currentTarget.blur();
          }
        }}
        className="w-20 rounded-lg border border-[#CBD5E1] bg-white px-2 py-1.5 text-right text-sm tabular-nums text-[#0F172A] shadow-sm focus:border-[#2563EB] focus:outline-none focus:ring-2 focus:ring-[#2563EB]/10"
      />
      {suffix ? <span className="text-xs text-[#64748B]">{suffix}</span> : null}
      <span className="w-4" aria-live="polite">
        {status === "saving" ? (
          <span
            className="block h-3.5 w-3.5 animate-spin rounded-full border-2 border-[#CBD5E1] border-t-[#2563EB]"
            aria-label="Saving"
          />
        ) : null}
        {status === "saved" ? (
          <CheckCircleIcon
            className="h-4 w-4 text-[#16A34A]"
            aria-label="Saved"
          />
        ) : null}
        {status === "error" ? (
          <ExclamationCircleIcon
            className="h-4 w-4 text-[#DC2626]"
            aria-label="Save failed"
          />
        ) : null}
      </span>
    </div>
  );
}
